// src/pages/Home.tsx
//
// Public landing page. Cinematic hero using the BENCHMARK hero
// photo (vendored from benchmark-av.be), category grid, stats,
// CTA. Dark governance plane.
//
// Z-index layering in the hero:
//   - z-index 0: the <picture> background + dark overlay + teal tint
//     (all absolutely positioned)
//   - z-index 10: the text content (relative)
// The section itself has NO background-color, so the image shows.
// The body's dark canvas (bg-surface-canvas) shows BEHIND the image.

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Video, Volume2, Frame, Waves, Plug } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";

const categoryIcons = {
  video: Video,
  audio: Volume2,
  screens: Frame,
  acoustic: Waves,
  accessories: Plug,
} as const;

const categoryOrder = ["video", "audio", "screens", "acoustic", "accessories"] as const;

export function Home() {
  const { t } = useTranslation();

  return (
    <div>
      {/* === HERO === */}
      <section className="relative min-h-[480px] overflow-hidden border-b border-border sm:min-h-[560px] lg:min-h-[640px]">
        {/* Background photo with art direction (responsive srcset) */}
        <picture aria-hidden className="absolute inset-0 z-0">
          <source media="(min-width: 1280px)" srcSet="/hero-lg.jpg" />
          <source media="(min-width: 640px)" srcSet="/hero-md.jpg" />
          <img
            src="/hero-sm.jpg"
            alt=""
            className="h-full w-full object-cover"
            style={{ objectPosition: "center 55%" }}
            loading="eager"
            fetchPriority="high"
          />
        </picture>

        {/* Dark overlay so text is legible against the photo */}
        <div
          aria-hidden
          className="absolute inset-0 z-0 bg-gradient-to-b from-bg-surface-canvas/85 via-bg-surface-canvas/60 to-bg-surface-canvas"
        />
        {/* Subtle teal tint to keep BENCHMARK brand identity */}
        <div
          aria-hidden
          className="absolute inset-0 z-0"
          style={{
            background:
              "radial-gradient(ellipse at top right, rgba(80, 200, 180, 0.12) 0%, transparent 50%)",
          }}
        />

        <div className="container relative z-10 flex min-h-[480px] items-center py-20 sm:min-h-[560px] sm:py-28 lg:min-h-[640px] lg:py-32">
          <div className="max-w-3xl">
            <Badge variant="teal" size="sm" className="mb-4 backdrop-blur-sm">
              {t("home.stats.brands")}
            </Badge>
            <h1 className="text-4xl font-510 leading-tight tracking-tighter text-fg-primary sm:text-5xl lg:text-6xl">
              {t("home.hero.headline")}
            </h1>
            <p className="mt-4 max-w-2xl text-base text-fg-secondary sm:text-lg">
              {t("home.hero.subline")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button href="/catalog" size="lg">
                {t("home.hero.cta")}
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <Button href="/stores" variant="secondary" size="lg">
                {t("nav.stores")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* === CATEGORIES === */}
      <section className="border-b border-border py-16 sm:py-20">
        <div className="container">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <h2 className="text-2xl font-510 tracking-tight text-fg-primary">
                {t("home.exploreTitle", "Explore by category")}
              </h2>
              <p className="mt-1 text-sm text-fg-tertiary">
                {t("home.exploreSub", "Professional gear for every use case")}
              </p>
            </div>
            <Link
              to="/catalog"
              className="hidden items-center gap-1 text-sm text-fg-secondary hover:text-fg-primary sm:flex"
            >
              {t("home.viewAll", "View all")}{" "}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {categoryOrder.map((key) => {
              const Icon = categoryIcons[key];
              return (
                <Link
                  key={key}
                  to={`/catalog?category=${key}`}
                  className="group"
                >
                  <Card className="h-full transition-colors group-hover:border-benchmark-teal/40">
                    <div className="flex flex-col items-start gap-3 p-5">
                      <div className="flex h-9 w-9 items-center justify-center rounded bg-benchmark-teal/15 ring-1 ring-benchmark-teal/30">
                        <Icon className="h-4 w-4 text-benchmark-teal-bright" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-sm font-510 text-fg-primary">
                          {t(`home.categories.${key}`)}
                        </div>
                        <div className="mt-0.5 text-xs text-fg-tertiary">
                          {t("home.browse", "Browse →")}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* === STATS === */}
      <section className="py-16 sm:py-20">
        <div className="container">
          <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-3">
            <div className="bg-bg-panel p-8">
              <div className="text-3xl font-510 tracking-tighter text-fg-primary">
                {t("home.stats.brands")}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wider text-fg-tertiary">
                {t("home.statCurated", "Curated selection")}
              </div>
            </div>
            <div className="bg-bg-panel p-8">
              <div className="text-3xl font-510 tracking-tighter text-fg-primary">
                {t("home.stats.experience")}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wider text-fg-tertiary">
                {t("home.statIndustry", "In the industry")}
              </div>
            </div>
            <div className="bg-bg-panel p-8">
              <div className="text-3xl font-510 tracking-tighter text-benchmark-teal-bright">
                {t("home.stats.distribution")}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wider text-fg-tertiary">
                {t("home.statCommitment", "Our commitment")}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
